/**
 * Decisiones del formulario de país/provincia/ciudad, sin React.
 *
 * Están aquí porque son las que se han roto más veces: qué provincia propone una ciudad, y si un
 * código de país que no está en el catálogo se pinta o se oculta.
 */

export interface PaisOpcion { code: string; nameEs: string }
export interface RegionOpcion { code: string; name: string }
export interface CiudadOpcion { name: string; provinceCode: string | null }
export interface PostalLookup {
  code: string; municipality: string; provinceCode: string | null; provinceName: string | null;
}

/** Campos de una dirección que un código postal puede rellenar. */
export interface CamposDireccion { city: string; regionCode: string; province: string }

/**
 * Provincia que propone una ciudad del catálogo.
 *
 * Devuelve `null` —y por tanto no se toca nada— cuando el usuario ya eligió una provincia, cuando la
 * ciudad no está en el catálogo o cuando su código no existe en el país (el catálogo de ciudades
 * trae `province_code` de GeoNames y no siempre coincide con nuestras regiones). Guardar un código
 * que no existe daría 400 al guardar la ficha.
 */
export function provinciaDesdeCiudad(
  ciudades: readonly CiudadOpcion[],
  provincias: readonly RegionOpcion[],
  nombreCiudad: string,
  regionActual: string | null | undefined
): { code: string; name: string } | null {
  if (regionActual) return null;
  const ciudad = ciudades.find((item) => item.name === nombreCiudad);
  if (!ciudad?.provinceCode) return null;
  const provincia = provincias.find((item) => item.code === ciudad.provinceCode);
  return provincia ? { code: provincia.code, name: provincia.name } : null;
}

/**
 * ¿Hay que pintar una opción para el país guardado aunque no esté en el catálogo?
 *
 * Un código antiguo o inventado (`UK` cuando el catálogo ya no lo tiene) tiene que verse: si no, el
 * desplegable dice «Sin país» mientras el formulario guarda otra cosa.
 */
export function paisFueraDelCatalogo(valor: string, paises: readonly PaisOpcion[]): boolean {
  if (!valor) return false;
  return !paises.some((pais) => pais.code === valor);
}

/**
 * Qué rellena un código postal cuando lo reconoce el catálogo.
 *
 * - **Provincia**: se pone siempre, porque el código postal la determina (en España los dos primeros
 *   dígitos son la provincia) y una provincia mal puesta da un 400 al guardar.
 * - **Ciudad**: solo si está vacía. Si el usuario ya escribió la suya (una pedanía, un barrio), el
 *   código postal no la pisa: eso borraba trabajo delante de sus ojos.
 * - Si la provincia que trae el catálogo no está en la lista del país cargada por la pantalla, no se
 *   toca nada: guardar un código que no existe daría 400.
 */
export function aplicarCodigoPostal(
  campos: CamposDireccion,
  hallazgo: PostalLookup | null,
  provincias: readonly RegionOpcion[]
): Partial<CamposDireccion> {
  if (!hallazgo) return {};
  const cambios: Partial<CamposDireccion> = {};
  const provincia = hallazgo.provinceCode
    ? provincias.find((item) => item.code === hallazgo.provinceCode)
    : undefined;
  if (provincia) {
    cambios.regionCode = provincia.code;
    cambios.province = provincia.name;
  }
  if (!campos.city.trim() && hallazgo.municipality) cambios.city = hallazgo.municipality;
  return cambios;
}
